/**
 * Staff management — clinic members + invitation lifecycle.
 *
 * Owners invite via a generated link (no email infra). The invitee opens
 * the public /invite/:token URL, sets a password, and joins the clinic.
 */
import { z } from "zod";

// These are the ONLY clinic-member roles. There is intentionally no separate
// "admin" role and no "patient" member role:
//   • Delegated administration (an office manager who configures the clinic
//     without being the owner) is modelled via per-member permission OVERRIDES
//     (`clinic_members.permissions_override`) — an owner grants a member any
//     subset of permissions — which is finer-grained than a fixed admin role.
//   • Patients are NOT members; they reach their data through the patient
//     portal (token-based), never via a clinic_members row.
export const clinicRoleSchema = z.enum([
	"owner",
	"doctor",
	"receptionist",
	"accountant",
	"nurse",
	// Lab-facility role: the equivalent of a doctor for a laboratory tenant.
	"lab_tech",
]);
export type ClinicRole = z.infer<typeof clinicRoleSchema>;

// ===== Members =====

export interface ClinicMemberApi {
	id: string;
	user_id: string;
	full_name: string;
	email: string | null;
	phone: string | null;
	role: ClinicRole;
	title: string | null;
	specialty: string | null;
	/** Prescriber's medical-syndicate / registration number, printed on prescriptions. Null = not set. */
	license_number: string | null;
	/** Doctor commission percentage as a decimal string ("0".."100"). Null = no commission. */
	commission_pct: string | null;
	is_active: boolean;
	joined_at: string;
	deactivated_at: string | null;
}

// Owner is intentionally NOT inviteable — there's only one per clinic,
// created at signup. Roles below are the inviteable set.
export const inviteableRoleSchema = z.enum([
	"doctor",
	"receptionist",
	"accountant",
	"nurse",
	"lab_tech",
]);
export type InviteableRole = z.infer<typeof inviteableRoleSchema>;

/**
 * Commission percentage validator: accepts "" / null to clear, or a numeric
 * string 0–100 with up to 2 decimal places. Stored verbatim in DB
 * (numeric-as-text for precision).
 */
const commissionPctSchema = z
	.string()
	.regex(
		/^(100(\.0{1,2})?|\d{1,2}(\.\d{1,2})?)$/u,
		"Must be 0–100 with up to 2 decimals",
	)
	.nullish();

export const updateMemberInputSchema = z.object({
	// Single-owner invariant: a member can be moved between staff roles but never
	// promoted TO owner (there is exactly one owner, created at signup). Using the
	// inviteable set excludes "owner" so the API rejects a second-owner attempt.
	role: inviteableRoleSchema.optional(),
	title: z.string().max(120).nullish(),
	specialty: z.string().max(120).nullish(),
	license_number: z.string().max(60).nullish(),
	commission_pct: commissionPctSchema,
	is_active: z.boolean().optional(),
});
export type UpdateMemberInput = z.infer<typeof updateMemberInputSchema>;

// ===== Invitations =====

export const createInvitationInputSchema = z.object({
	full_name: z.string().min(2).max(120),
	role: inviteableRoleSchema,
	title: z.string().max(120).optional(),
	specialty: z.string().max(120).optional(),
	contact: z.string().max(120).optional(),
});
export type CreateInvitationInput = z.infer<typeof createInvitationInputSchema>;

export interface InvitationApi {
	id: string;
	token: string;
	/** Convenience: full URL clients can copy/share via WhatsApp etc. */
	invite_url: string;
	role: ClinicRole;
	full_name: string;
	title: string | null;
	specialty: string | null;
	contact: string | null;
	expires_at: string;
	accepted_at: string | null;
	created_at: string;
}

/** Sanitized public payload for the accept page (no internals). */
export interface PublicInvitationApi {
	token: string;
	full_name: string;
	role: ClinicRole;
	title: string | null;
	clinic_name: { ar: string; en?: string };
	clinic_slug: string;
	expires_at: string;
}

export const acceptInvitationInputSchema = z.object({
	email: z.string().email().max(120),
	phone: z.string().max(40).optional(),
	password: z.string().min(8).max(128),
	preferred_language: z.enum(["ar", "en"]).default("ar"),
});
export type AcceptInvitationInput = z.infer<typeof acceptInvitationInputSchema>;
